import React, { useCallback, memo, useMemo } from 'react'
import { Box, Typography } from '@mui/material'

import TextModule from './ModuleTypes/TextModule'
import TextImageModule from './ModuleTypes/TextImageModule'
import TextVideoModule from './ModuleTypes/TextVideoModule'
import QuizModule from './ModuleTypes/QuizModule'
import HtmlModule from './ModuleTypes/HtmlModule'
import { ModuleType, moduleTypes } from './ModuleTypes/ModuleTypesEnum'
import { useForm } from 'react-final-form'
import {
  backgroundThemes,
  DEFAULT_DARK_TEXT_COLOR,
  DEFAULT_LIGHT_THEME_CARD_BACKGROUND,
  getModuleTheme
} from '../../../common/constants/themeConstants'

const MemoizedTextModule = memo(TextModule)
const MemoizedTextImageModule = memo(TextImageModule)
const MemoizedTextVideoModule = memo(TextVideoModule)
const MemoizedQuizModule = memo(QuizModule)
const MemoizedHtmlModule = memo(HtmlModule)

const TypeCard = memo(({ type, onClick, isCurrentThemeLight }) => (
  <Box
    sx={{
      ...styles.typeCard,
      background: isCurrentThemeLight ? DEFAULT_LIGHT_THEME_CARD_BACKGROUND : ' rgba(255, 255, 255, 0.1)',
      border: isCurrentThemeLight ? `1px solid #ececec` : 'none',
      ...(type.comingSoon ? styles.disabledCard : {}),
      position: 'relative',
      overflow: 'hidden'
    }}
    onClick={() => !type.comingSoon && onClick(type.id)}
  >
    {type.comingSoon && (
      <Box sx={styles.comingSoonRibbon}>
        <Typography sx={styles.comingSoonText}>Coming Soon</Typography>
      </Box>
    )}
    <Box sx={{ ...styles.typeIcon, color: isCurrentThemeLight ? DEFAULT_DARK_TEXT_COLOR : 'rgba(255, 255, 255, 0.7)' }}>
      {type.icon}
    </Box>
    <Typography
      sx={{ ...styles.typeTitle, color: isCurrentThemeLight ? DEFAULT_DARK_TEXT_COLOR : 'rgba(255, 255, 255, 1)' }}
    >
      {type.title}
    </Typography>
    <Typography
      sx={{ ...styles.description, color: isCurrentThemeLight ? DEFAULT_DARK_TEXT_COLOR : 'rgba(255, 255, 255, 1)' }}
    >
      {type.description}
    </Typography>
  </Box>
))

const Module = ({ step, theme, isActive, style, onClick, name, isPreview = false, completedModules }) => {
  const form = isPreview ? null : useForm()

  const moduleIndex = isPreview
    ? (step.sequenceNumber || 1) - 1
    : parseInt(name?.split('.')[1] || name?.match(/\[(\d+)\]/)?.[1] || '0', 10)
  const actualTheme = getModuleTheme(theme, moduleIndex)
  const currentThemeObject = actualTheme && backgroundThemes?.[actualTheme]
  const isCurrentThemeLight = currentThemeObject?.isLight

  const getInitialDataForType = useCallback(type => {
    switch (type) {
      case ModuleType.TEXT:
        return { title: '', message: '' }
      case ModuleType.TEXT_IMAGE:
        return { title: '', message: '', imageUrl: '' }
      case ModuleType.VIDEO:
        return { title: '', url: '', alignment: 'LEFT' }
      case ModuleType.QUIZ: {
        return {
          context: '',
          comment: '',
          answers: []
        }
      }
      case ModuleType.HTML:
        return { data: '' }
      default:
        return {}
    }
  }, [])

  const handleSelectType = useCallback(
    type => {
      if (isPreview || !form) return

      try {
        const initialData = getInitialDataForType(type)

        form.batch(() => {
          form.change(`${name}.type`, type)
          if (step.sequenceNumber === undefined) {
            form.change(`${name}.sequenceNumber`, 1)
          }
          if (step.alignment === undefined) {
            form.change(`${name}.alignment`, 'LEFT')
          }

          Object.entries(initialData).forEach(([key, value]) => {
            form.change(`${name}.${key}`, value)
          })
        })
      } catch (error) {
        console.error('Error selecting module type:', error)
      }
    },
    [form, name, getInitialDataForType, step, isPreview]
  )

  const typeSelector = useMemo(
    () => (
      <Box display={'flex'} flexDirection={'column'} gap={2} width={'100%'} justifyContent={'center'} height={'100%'}>
        <Box display={'flex'} flexDirection={'column'} gap={2} width={'100%'} justifyContent={'center'} height={'auto'}>
          <Typography
            color={isCurrentThemeLight ? DEFAULT_DARK_TEXT_COLOR : '#fff'}
            fontSize='20px'
            fontWeight={600}
            fontFamily='Plus Jakarta Sans'
          >
            Select a layout:
          </Typography>
          <Box sx={styles.typeSelector}>
            {moduleTypes.map(type => (
              <TypeCard
                key={`type-${type.id}`}
                type={type}
                onClick={handleSelectType}
                isCurrentThemeLight={isCurrentThemeLight}
              />
            ))}
          </Box>
        </Box>
      </Box>
    ),
    [handleSelectType, isCurrentThemeLight]
  )

  const moduleContent = useMemo(() => {
    if (!step.type) {
      return typeSelector
    }

    const moduleProps = {
      completedModules,
      name,
      isPreview,
      data: isPreview ? step : undefined,
      isCurrentThemeLight
    }

    switch (step.type) {
      case ModuleType.TEXT:
        return <MemoizedTextModule {...moduleProps} />
      case ModuleType.TEXT_IMAGE:
        return <MemoizedTextImageModule {...moduleProps} />
      case ModuleType.VIDEO:
        return <MemoizedTextVideoModule {...moduleProps} />
      case ModuleType.QUIZ:
        return <MemoizedQuizModule {...moduleProps} />
      case ModuleType.HTML:
        return <MemoizedHtmlModule {...moduleProps} />
      default:
        return <Typography>Unknown module type</Typography>
    }
  }, [step, name, typeSelector, isPreview, isCurrentThemeLight])

  const combinedStyles = useMemo(
    () => ({
      ...styles.module,
      ...style,
      backgroundColor: '#fff',
      backdropFilter: 'blur(70px)',
      borderColor: isActive ? '#6123FF' : 'transparent',
      backgroundImage:
        actualTheme && backgroundThemes?.[actualTheme]
          ? backgroundThemes[actualTheme].image
            ? `url(${backgroundThemes[actualTheme].image})`
            : backgroundThemes[actualTheme].gradient
          : 'none',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      cursor: isPreview ? 'default' : 'pointer',
      paddingBottom: isPreview ? '12px' : '0px'
    }),
    [style, isActive, actualTheme, isPreview]
  )

  return (
    <Box sx={combinedStyles} onClick={isPreview ? undefined : onClick}>
      <Box sx={styles.content}>{moduleContent}</Box>
    </Box>
  )
}

const styles = {
  module: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: '16px',
    transition: 'all 0.3s ease',
    color: 'rgba(255, 255, 255, 0.3)',
    cursor: 'pointer',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  },
  title: {
    fontWeight: 600
  },
  content: {
    flex: 1,
    padding: '24px',
    overflow: 'auto',
    scrollBehavior: 'smooth',
    '&::-webkit-scrollbar': {
      width: '6px',
      height: '6px'
    },
    '&::-webkit-scrollbar-track': {
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '10px'
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: '10px',
      transition: 'background 0.2s ease'
    },

    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(97, 35, 255, 0.5) rgba(255, 255, 255, 0.1)'
  },
  typeSelector: {
    height: '100%',
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gridTemplateRows: 'repeat(2, 1fr)',
    gap: '16px',
    alignItems: 'center',
    justifyItems: 'center',
    borderRadius: '16px',
    padding: '20px'
  },
  typeCard: {
    padding: '12px',
    width: '180px',
    height: '160px',
    backdropFilter: 'blur(100px)',
    background: ' rgba(255, 255, 255, 0.1)',
    borderRadius: '16px',
    borderImageSource:
      'linear-gradient(158.03deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.3) 50%, rgba(255, 255, 255, 0.1) 100%)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
    }
  },
  disabledCard: {
    opacity: 0.7,
    cursor: 'not-allowed',
    '&:hover': {
      transform: 'none',
      boxShadow: 'none'
    }
  },
  comingSoonRibbon: {
    position: 'absolute',
    top: '20px',
    right: '-30px',
    transform: 'rotate(45deg)',
    backgroundColor: 'rgba(97, 35, 255, 0.9)',
    padding: '5px 30px',
    width: '150px',
    textAlign: 'center',
    boxShadow: '0 0 10px rgba(0,0,0,0.2)'
  },
  comingSoonText: {
    color: '#FFFFFF',
    fontWeight: 'bold',
    fontSize: '14px',
    textTransform: 'uppercase'
  },
  typeIcon: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '28px',
    '& svg': {
      width: '28px',
      height: '28px'
    }
  },
  typeTitle: {
    fontFamily: 'Plus Jakarta Sans',
    fontWeight: '600',
    fontSize: '14px',
    color: 'rgba(255, 255, 255, 1)',
    textAlign: 'center'
  },
  description: {
    fontFamily: 'Plus Jakarta Sans',
    fontWeight: 'lighter',
    fontSize: '11px',
    color: 'rgba(255, 255, 255, 1)',
    textAlign: 'center',
    lineHeight: '1.3',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical'
  }
}

export default memo(Module)
